import { AlertCircle, FileText, Plus, RefreshCw, Search } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';

import { CadrageReadWithTeam, Domain, ForgeApplicationWithContext, UseCaseSummary } from '../../types';
import { Link, useNavigate } from 'react-router-dom';
import cadrageService from '../../services/cadrageService';
import { Button } from '@/components/Common/button';
import { PageLayout } from '@/components/Layout/PageLayout';
import { DomainSelector } from '@/components/Common/DomainSelector';
import { UseCaseSelector } from '@/components/AIExtraction/UseCaseSelector';
import { domainService } from '@/services/domainService';
import forgeService from '@/services/forgeService';
import { useCaseService } from '@/services/useCaseService';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';
import { useTranslation } from 'react-i18next';

const CadragesListPage: React.FC = () => {
  const [cadrages, setCadrages] = useState<CadrageReadWithTeam[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);
  const [applications, setApplications] = useState<ForgeApplicationWithContext[]>([]);
  const [forgeNameById, setForgeNameById] = useState<Record<string, string>>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isApplicationsLoading, setIsApplicationsLoading] = useState<boolean>(true);
  const [isForgesLoading, setIsForgesLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const forgeApiEnabled = useForgeApiEnabled();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [cadragesData, domainsData, useCasesData] = await Promise.all([
          cadrageService.getCadrages(),
          domainService.getUserDomains(),
          useCaseService.list(),
        ]);
        setCadrages(cadragesData);
        setDomains(domainsData);
        setUseCases(useCasesData);
      } catch (err: any) {
        console.error('Erreur lors de la récupération des cadrages:', err);
        setError(err.message || t('cadrages.list.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Forge applications in the background — used only to decorate the
  // "Linked app" column, so the table doesn't need to wait on it.
  useEffect(() => {
    if (forgeApiEnabled === null) return;
    if (!forgeApiEnabled) {
      setIsApplicationsLoading(false);
      return;
    }
    let ignore = false;
    setIsApplicationsLoading(true);
    forgeService
      .listApplicationsWithContext()
      .then((data) => {
        if (!ignore) setApplications(data);
      })
      .catch((err) => {
        console.warn('Unable to load Forge applications:', err);
      })
      .finally(() => {
        if (!ignore) setIsApplicationsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [forgeApiEnabled]);

  // Load Forge names in the background — used to decorate the "Forge" column.
  useEffect(() => {
    if (forgeApiEnabled === null) return;
    if (!forgeApiEnabled) {
      setIsForgesLoading(false);
      return;
    }
    let ignore = false;
    setIsForgesLoading(true);
    forgeService
      .listForgesWithDomains()
      .then((data) => {
        if (ignore) return;
        const map: Record<string, string> = {};
        for (const forge of data) {
          map[forge.forge_id] = forge.display_name || forge.name || forge.forge_id;
        }
        setForgeNameById(map);
      })
      .catch((err) => {
        console.warn('Unable to load Forge names:', err);
      })
      .finally(() => {
        if (!ignore) setIsForgesLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [forgeApiEnabled]);

  const domainNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of domains) {
      map[d.id] = d.name;
    }
    return map;
  }, [domains]);

  const domainById = useMemo(() => {
    const map: Record<string, Domain> = {};
    for (const d of domains) {
      map[d.id] = d;
    }
    return map;
  }, [domains]);

  const useCaseById = useMemo(() => {
    const map: Record<string, UseCaseSummary> = {};
    for (const uc of useCases) {
      map[uc.id] = uc;
    }
    return map;
  }, [useCases]);

  const applicationByUseCaseId = useMemo(() => {
    const map: Record<string, ForgeApplicationWithContext> = {};
    for (const app of applications) {
      if (app.use_case_id) {
        map[app.use_case_id] = app;
      }
    }
    return map;
  }, [applications]);

  const filteredCadrages = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return cadrages.filter((cadrage) => {
      // Only show active cadrages (not historical versions)
      if (!cadrage.is_active) {
        return false;
      }

      const relatedUseCase = cadrage.use_case_id ? useCaseById[cadrage.use_case_id] : undefined;
      const matchQuery =
        cadrage.name.toLowerCase().includes(q) ||
        (cadrage.description && cadrage.description.toLowerCase().includes(q)) ||
        (cadrage.company_name && cadrage.company_name.toLowerCase().includes(q)) ||
        (relatedUseCase &&
          (relatedUseCase.name.toLowerCase().includes(q) ||
            (relatedUseCase.short_description || '').toLowerCase().includes(q)));

      const matchDomain =
        !selectedDomainId ||
        (relatedUseCase && (relatedUseCase.domain_id || null) === selectedDomainId);

      const matchUseCase = !selectedUseCaseId || cadrage.use_case_id === selectedUseCaseId;

      return matchQuery && matchDomain && matchUseCase;
    });
  }, [cadrages, searchQuery, selectedDomainId, selectedUseCaseId, useCaseById]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t('global.notAvailable');
    try {
      return new Date(dateString).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return t('cadrages.list.invalidDate');
    }
  };

  return (
    <PageLayout
      icon={<FileText className="h-6 w-6" />}
      title={t('cadrages.list.title', 'Cadrages')}
      subtitle={t(
        'cadrages.list.subtitle',
        "Vue liste simple de tous vos cadrages, filtrables par domaine et cas d'usage.",
      )}
    >
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t(
                'cadrages.list.searchPlaceholder',
                "Rechercher un cadrage, un client ou un cas d'usage...",
              )}
              className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[160px]">
              <div className="mb-1 text-sm font-medium">{t('global.domain')}</div>
              <DomainSelector
                selectedDomainId={selectedDomainId}
                onDomainChange={(d: Domain | null) => {
                  setSelectedDomainId(d ? d.id : null);
                  setSelectedUseCaseId(null);
                }}
              />
            </div>
            <div className="min-w-[180px]">
              <div className="mb-1 text-sm font-medium">
                {t('cadrages.list.filters.useCase', "Cas d'usage")}
              </div>
              <UseCaseSelector
                domainId={selectedDomainId}
                selectedUseCaseId={selectedUseCaseId}
                onUseCaseChange={(uc: UseCaseSummary | null) =>
                  setSelectedUseCaseId(uc ? uc.id : null)
                }
              />
            </div>
            <Button asChild className="flex items-center gap-2 shrink-0">
              <Link to="/cadrages/new">
                <Plus className="h-4 w-4" />
                {t('cadrages.list.new', 'Nouveau cadrage')}
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-10">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">
            {t('cadrages.list.loading', 'Chargement des cadrages...')}
          </span>
        </div>
      )}

      {error && (
        <div
          className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold mr-2">
            {t('global.error', 'Erreur')}
          </strong>
          <span className="block sm:inline">{error}</span>
          <button
            type="button"
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <AlertCircle className="h-5 w-5" />
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="rounded-xl border border-border bg-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            {filteredCadrages.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">
                {t(
                  'cadrages.list.empty',
                  'Aucun cadrage ne correspond aux critères sélectionnés.',
                )}
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[220px] max-w-[220px]">
                      {t('cadrages.list.table.cadrage', 'Cadrage')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.domain')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('cadrages.list.table.useCase', "Cas d'usage")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.client', 'Client')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('cadrages.list.table.forge', 'Forge')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('cadrages.list.table.linkedApp', 'Linked app')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('cadrages.list.table.createdAt', 'Créé le')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {filteredCadrages.map((cadrage) => {
                    const relatedUseCase = cadrage.use_case_id
                      ? useCaseById[cadrage.use_case_id]
                      : undefined;
                    const domainLabel =
                      relatedUseCase && relatedUseCase.domain_id
                        ? domainNameById[relatedUseCase.domain_id] ||
                          relatedUseCase.domain_id
                        : '—';
                    const linkedApp = cadrage.use_case_id
                      ? applicationByUseCaseId[cadrage.use_case_id]
                      : undefined;
                    const forgeIdFromDomain =
                      relatedUseCase && relatedUseCase.domain_id
                        ? domainById[relatedUseCase.domain_id]?.forge_id || null
                        : null;
                    const forgeId = linkedApp?.forge_id || forgeIdFromDomain || null;
                    const forgeLabel = forgeId ? forgeNameById[forgeId] || forgeId : '—';
                    return (
                      <tr
                        key={cadrage.id}
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => navigate(`/cadrages/${cadrage.id}`)}
                      >
                        <td className="px-4 py-3 max-w-[220px]">
                          <div className="text-sm font-semibold text-foreground truncate" title={cadrage.name}>
                            {cadrage.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {domainLabel}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {relatedUseCase ? relatedUseCase.name : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {cadrage.company_name || t('global.notAvailable')}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {isForgesLoading && cadrage.use_case_id ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              {t('global.loading', 'Chargement...')}
                            </span>
                          ) : (
                            forgeLabel
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {isApplicationsLoading && cadrage.use_case_id ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              {t('global.loading', 'Chargement...')}
                            </span>
                          ) : linkedApp ? (
                            <Link
                              to={`/applications/${linkedApp.app_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline"
                              title={linkedApp.name}
                            >
                              {linkedApp.name}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {formatDate(cadrage.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default CadragesListPage;
